// Minimal fragmented-MP4 muxer for a single AVC video track.
// Ported from dragon/html/playback.js (Brad's reference impl). Used by the
// export-with-overlay pipeline: re-encodes each playback frame (with HUD +
// detection overlays drawn into canvas pixels) into a downloadable .mp4.
//
// Output is fragmented MP4 (moof/mdat per GOP), playable in every modern
// browser + most media players. We don't write stbl-based non-frag output
// because that'd require holding the entire encoded bitstream in memory
// twice — fragmented mode streams chunks straight in.
export class Mp4Muxer {
    width;
    height;
    timescale;
    description = null;
    chunks = [];
    trackId = 1;
    constructor(opts) {
        this.width = opts.width;
        this.height = opts.height;
        this.timescale = opts.timescale;
    }
    setDecoderConfig(description) {
        if (this.description)
            return;
        const src = description instanceof Uint8Array
            ? description
            : new Uint8Array(description instanceof ArrayBuffer
                ? description
                : description.buffer.slice(description.byteOffset, description.byteOffset + description.byteLength));
        this.description = new Uint8Array(src.byteLength);
        this.description.set(src);
    }
    addChunk(chunk) {
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        this.chunks.push({
            data: buf,
            timestamp: chunk.timestamp,
            duration: chunk.duration ?? 0,
            isKey: chunk.type === "key",
        });
    }
    finalize() {
        if (!this.description)
            throw new Error("no decoder config received from encoder");
        if (this.chunks.length === 0)
            throw new Error("no chunks to mux");
        const parts = [];
        parts.push(this.ftyp());
        parts.push(this.moov());
        // Group chunks into fragments — one per GOP (each starts with keyframe).
        let seq = 1;
        for (const frag of this.groupFragments()) {
            const { moof, mdat } = this.buildFragment(frag, seq++);
            parts.push(moof);
            parts.push(mdat);
        }
        return new Blob(parts, { type: "video/mp4" });
    }
    groupFragments() {
        const frags = [];
        let cur = null;
        for (const c of this.chunks) {
            if (c.isKey || !cur) {
                if (cur)
                    frags.push(cur);
                cur = [c];
            }
            else {
                cur.push(c);
            }
        }
        if (cur)
            frags.push(cur);
        return frags;
    }
    buildFragment(chunks, seq) {
        const firstTs = chunks[0].timestamp;
        const baseMediaDecodeTime = this.usToTs(firstTs);
        const sampleDurations = chunks.map(c => this.usToTs(c.duration));
        const sampleSizes = chunks.map(c => c.data.byteLength);
        const mdatBodySize = sampleSizes.reduce((a, b) => a + b, 0);
        const mdatSize = 8 + mdatBodySize;
        // trun flags: data_offset | first_sample_flags | sample_duration | sample_size
        const trunBody = B.concat(B.u32(chunks.length), B.s32(0), // data_offset (patched after)
        B.u32(0x02000000), // first_sample_flags = I-frame
        ...chunks.flatMap((_c, i) => [B.u32(sampleDurations[i]), B.u32(sampleSizes[i])]));
        const trun = B.fullBox("trun", 0, 0x000305, trunBody);
        const tfhd = B.fullBox("tfhd", 0, 0x020000, B.u32(this.trackId));
        const tfdt = B.fullBox("tfdt", 1, 0, B.u64(baseMediaDecodeTime));
        const traf = B.box("traf", tfhd, tfdt, trun);
        const mfhd = B.fullBox("mfhd", 0, 0, B.u32(seq));
        const moof = B.box("moof", mfhd, traf);
        const dataOffset = moof.byteLength + 8;
        this.patchTrunDataOffset(moof, dataOffset);
        const mdat = B.concat(B.u32(mdatSize), B.type("mdat"), ...chunks.map(c => c.data));
        return { moof, mdat };
    }
    patchTrunDataOffset(moof, dataOffset) {
        let p = 8;
        const end = moof.byteLength;
        while (p + 8 <= end) {
            const size = (moof[p] << 24) | (moof[p + 1] << 16) | (moof[p + 2] << 8) | moof[p + 3];
            const type = String.fromCharCode(moof[p + 4], moof[p + 5], moof[p + 6], moof[p + 7]);
            if (type === "traf") {
                let q = p + 8;
                const tend = p + size;
                while (q + 8 <= tend) {
                    const qs = (moof[q] << 24) | (moof[q + 1] << 16) | (moof[q + 2] << 8) | moof[q + 3];
                    const qt = String.fromCharCode(moof[q + 4], moof[q + 5], moof[q + 6], moof[q + 7]);
                    if (qt === "trun") {
                        const dOff = q + 8 + 4 + 4;
                        moof[dOff] = (dataOffset >>> 24) & 0xff;
                        moof[dOff + 1] = (dataOffset >>> 16) & 0xff;
                        moof[dOff + 2] = (dataOffset >>> 8) & 0xff;
                        moof[dOff + 3] = dataOffset & 0xff;
                        return;
                    }
                    q += qs;
                }
            }
            p += size;
        }
    }
    usToTs(us) { return Math.round(us * this.timescale / 1e6); }
    ftyp() {
        return B.box("ftyp", B.type("isom"), B.u32(512), B.type("isom"), B.type("iso2"), B.type("avc1"), B.type("mp41"));
    }
    moov() { return B.box("moov", this.mvhd(), this.trak(), this.mvex()); }
    mvhd() {
        return B.fullBox("mvhd", 0, 0, B.u32(0), B.u32(0), B.u32(this.timescale), B.u32(0), B.u32(0x00010000), B.u16(0x0100), B.u16(0), B.u32(0), B.u32(0), B.u32(0x00010000), B.u32(0), B.u32(0), B.u32(0), B.u32(0x00010000), B.u32(0), B.u32(0), B.u32(0), B.u32(0x40000000), B.u32(0), B.u32(0), B.u32(0), B.u32(0), B.u32(0), B.u32(0), B.u32(this.trackId + 1));
    }
    trak() {
        const tkhd = B.fullBox("tkhd", 0, 0x7, B.u32(0), B.u32(0), B.u32(this.trackId), B.u32(0), B.u32(0), B.u32(0), B.u32(0), B.u16(0), B.u16(0), B.u16(0), B.u16(0), B.u32(0x00010000), B.u32(0), B.u32(0), B.u32(0), B.u32(0x00010000), B.u32(0), B.u32(0), B.u32(0), B.u32(0x40000000), B.u32(this.width << 16), B.u32(this.height << 16));
        return B.box("trak", tkhd, this.mdia());
    }
    mdia() {
        const mdhd = B.fullBox("mdhd", 0, 0, B.u32(0), B.u32(0), B.u32(this.timescale), B.u32(0), B.u16(0x55c4), B.u16(0));
        const name = new TextEncoder().encode("VideoHandler\0");
        const hdlr = B.fullBox("hdlr", 0, 0, B.u32(0), B.type("vide"), B.u32(0), B.u32(0), B.u32(0), name);
        return B.box("mdia", mdhd, hdlr, this.minf());
    }
    minf() {
        const vmhd = B.fullBox("vmhd", 0, 1, B.u16(0), B.u16(0), B.u16(0), B.u16(0));
        const url = B.fullBox("url ", 0, 0x1);
        const dref = B.fullBox("dref", 0, 0, B.u32(1), url);
        const dinf = B.box("dinf", dref);
        return B.box("minf", vmhd, dinf, this.stbl());
    }
    stbl() {
        return B.box("stbl", B.fullBox("stsd", 0, 0, B.u32(1), this.avc1()), B.fullBox("stts", 0, 0, B.u32(0)), B.fullBox("stsc", 0, 0, B.u32(0)), B.fullBox("stsz", 0, 0, B.u32(0), B.u32(0)), B.fullBox("stco", 0, 0, B.u32(0)));
    }
    avc1() {
        const body = B.concat(new Uint8Array(6), B.u16(1), B.u16(0), B.u16(0), B.u32(0), B.u32(0), B.u32(0), B.u16(this.width), B.u16(this.height), B.u32(0x00480000), B.u32(0x00480000), B.u32(0), B.u16(1), new Uint8Array(32), B.u16(0x0018), B.u16(0xffff));
        return B.box("avc1", body, B.box("avcC", this.description));
    }
    mvex() {
        const trex = B.fullBox("trex", 0, 0, B.u32(this.trackId), B.u32(1), B.u32(0), B.u32(0), B.u32(0x00010000));
        return B.box("mvex", trex);
    }
}
const B = {
    u16: (n) => new Uint8Array([(n >>> 8) & 0xff, n & 0xff]),
    u32: (n) => new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]),
    s32: (n) => {
        const u = (n | 0) >>> 0;
        return new Uint8Array([(u >>> 24) & 0xff, (u >>> 16) & 0xff, (u >>> 8) & 0xff, u & 0xff]);
    },
    u64: (n) => {
        const hi = Math.floor(n / 4294967296);
        const lo = n >>> 0;
        return new Uint8Array([
            (hi >>> 24) & 0xff, (hi >>> 16) & 0xff, (hi >>> 8) & 0xff, hi & 0xff,
            (lo >>> 24) & 0xff, (lo >>> 16) & 0xff, (lo >>> 8) & 0xff, lo & 0xff,
        ]);
    },
    type: (s) => new Uint8Array([s.charCodeAt(0), s.charCodeAt(1), s.charCodeAt(2), s.charCodeAt(3)]),
    concat(...parts) {
        let total = 0;
        for (const p of parts)
            total += p.byteLength;
        const out = new Uint8Array(total);
        let o = 0;
        for (const p of parts) {
            out.set(p, o);
            o += p.byteLength;
        }
        return out;
    },
    box(type, ...children) {
        const body = B.concat(...children);
        return B.concat(B.u32(8 + body.byteLength), B.type(type), body);
    },
    fullBox(type, version, flags, ...children) {
        const vf = new Uint8Array(4);
        vf[0] = version & 0xff;
        vf[1] = (flags >>> 16) & 0xff;
        vf[2] = (flags >>> 8) & 0xff;
        vf[3] = flags & 0xff;
        return B.box(type, vf, ...children);
    },
};
